import { MepInsightsVesselEmissionsComparedWithTarget, MepInsightsRequest } from "../../../../../../../../models/mep/mep-insights.models";
import { MepInsightsService } from "../../../../../../../../services/mep/mep-insights.service";
import { MepEmissionsTargetColourEnum as MepEmissionsTargetColourIdsEnum, MepLowTargetColor, MepMediumTargetColor, MepHighTargetColor } from "../../../../../../../../constants/mep/mep.constants";
import * as Enumerable from "linq";
const am4core = await import("@amcharts/amcharts4/core");
const am4charts = await import("@amcharts/amcharts4/charts");
import { MepEventMessages, MepTabsEnum } from "../../../../../../../../models/mep/mep.models";
import { EventAggregator } from "aurelia-event-aggregator";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { useView } from "aurelia-framework";

@useView('views/pages/modules/mep/tabs/mep-insights/partials/mep-insights-vessel-emissions-comparison/mep-insights-vessel-emissions-comparison.html')
export class MepInsightsVesselEmissionsComparison {
    emissionsTargets: any;
    vesselEmissionsComparison: MepInsightsVesselEmissionsComparedWithTarget[];
    vesselEmissionsComparisonPieChartViewModel: MepInsightsVesselEmissionsComparisonPieChartViewModel;
    isVesselEmissionsComparisonLoading: boolean;
    isMepTrial: boolean = false;

    constructor(private mepInsightsService: MepInsightsService, private eventAggregator: EventAggregator, isMepTrial: boolean, private configService: ConfigService) {
        this.isMepTrial = isMepTrial;
    }

    getMepInsightsVesselEmissionsComparedWithTarget(request: MepInsightsRequest): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            this.isVesselEmissionsComparisonLoading = true;

            this.mepInsightsService.getMepInsightsVesselEmissionsComparedWithTarget(request).then(response => {
                this.vesselEmissionsComparison = response.results;
                
                if (this.vesselEmissionsComparison && this.vesselEmissionsComparison.length > 0) {
                    this.vesselEmissionsComparisonPieChartViewModel = new MepInsightsVesselEmissionsComparisonPieChartViewModel(this.vesselEmissionsComparison, this.configService);
                    this.vesselEmissionsComparisonPieChartViewModel.initVesselEmissionsComparisonPieChart();
                }
                
                this.isVesselEmissionsComparisonLoading = false;
                resolve(null);
            }).catch(() => {
                this.isVesselEmissionsComparisonLoading = false;
                reject("MEP Insights Vessel Emissions - Error retrieving the vessel emissions comparison data!");
            });
        });
    }

    getTargetTitle(colourCode: number) {
        switch(colourCode) {
            case MepEmissionsTargetColourIdsEnum.Low:
                return "Within target";
            case MepEmissionsTargetColourIdsEnum.Medium:
                return "Exceed target by up to 15%";
            case MepEmissionsTargetColourIdsEnum.High:
                return "Exceed target over 15%";
        }

        return "No target set";
    }

    clearVesselEmissionsComparisonData() {
        this.vesselEmissionsComparison = null;
        this.vesselEmissionsComparisonPieChartViewModel = null;
    }

    navigateToEmissionTargets() {
        this.eventAggregator.publish(MepEventMessages.ChangeMepTab, {
            step: MepTabsEnum.EmissionTargets
        });
    }

}

export class MepInsightsVesselEmissionsComparisonPieChartViewModel {
    vesselEmissionsComparisonPieChart: any;

    constructor(private vesselEmissionsComparison: MepInsightsVesselEmissionsComparedWithTarget[], private configService: ConfigService) {
    }
    
    initVesselEmissionsComparisonPieChart(chartDivName: string = "piechartdiv") {
        //Create chart instance
        am4core.addLicense(this.configService.config.settings.amChartsLicense);
        this.vesselEmissionsComparisonPieChart = am4core.create(chartDivName, am4charts.PieChart);
        this.vesselEmissionsComparisonPieChart.innerRadius = 100;

        //create series
        const pieSeries = this.vesselEmissionsComparisonPieChart.series.push(new am4charts.PieSeries());
        pieSeries.dataFields.value = "vesselCountPerColourCode";
        pieSeries.dataFields.percentage = "vesselCountPercentages"
        pieSeries.dataFields.category = "colourCode";

        pieSeries.slices.template.stroke = new am4core.InterfaceColorSet().getFor("background");
        pieSeries.slices.template.strokeWidth = 2;
        pieSeries.slices.template.strokeOpacity = 1;

        const slice = pieSeries.slices.template;
        slice.states.getKey("hover").properties.scale = 1.1;
        slice.states.getKey("active").properties.shiftRadius = 0;
        
        pieSeries.alignLabels = false;
        pieSeries.ticks.template.disabled = true;
        pieSeries.labels.template.radius = am4core.percent(-20);
        pieSeries.labels.template.fill = am4core.color("white");
        pieSeries.labels.template.adapter.add("text", function(text, target) {
            if (target.dataItem && (target.dataItem.values.value.percent < 5)) {
                return "";
            }
            else if (target.dataItem && (target.dataItem.values.value.percent < 10)) {
                return "{percentage.formatNumber('#.#')}%";
            }
            else {
                return "{percentage.formatNumber('#.')}%";
            }
        });

        pieSeries.slices.template.adapter.add("tooltipText", function(tooltipText, target) {
            if (target.dataItem && (target.dataItem.values.value.percent == 0)) {
                return "";
            } else if (target.dataItem && (target.dataItem.values.value.percent < 1)) {
                return "<1%";
            }
            else {
                return "{percentage.formatNumber('#.#')}%";
            }
        });

        pieSeries.tooltip.adapter.add("disabled", function(disabled, target) {
            if (target.dataItem && (target.dataItem.values.value.percent > 5)) {
              return true;
            }
            return disabled;
        });

        //Add the data
        const totalValue = Enumerable.from(this.vesselEmissionsComparison).sum(x => x.vesselCountPerColourCode);
        const chartData = Enumerable.from(this.vesselEmissionsComparison)
            .select(x => {
                return {
                    vesselCountPerColourCode: x.vesselCountPerColourCode,
                    colourCode: x.colourCode.toString(),
                    vesselCountPercentages: this.getPercentages(x.vesselCountPerColourCode, totalValue)
                }
            })
        .toArray();
        this.vesselEmissionsComparisonPieChart.data = chartData;

        const colorSet = new am4core.ColorSet();
        colorSet.list = Enumerable.from(this.vesselEmissionsComparison).select(x => this.getColor(x.colourCode)).toArray().map(function (color) {
            return am4core.color(color);
        });
        
        pieSeries.colors = colorSet;
    }

    getPercentages(value: number, totalValue: number) {
        if (!value || !totalValue)
            return 0;
            
        const computedTotalValue = totalValue == 0 ? 1 : totalValue;
        const percentages = Number(value / computedTotalValue * 100).toFixed(2);

        return percentages;
    }

    getColor(colourCode: number) {
        switch(colourCode) {
            case MepEmissionsTargetColourIdsEnum.Low:
                return MepLowTargetColor;
            case MepEmissionsTargetColourIdsEnum.Medium:
                return MepMediumTargetColor;
            case MepEmissionsTargetColourIdsEnum.High:
                return MepHighTargetColor;
        }

        return "";
    }
}